// Header.jsx - Enhanced with navigation to Profile and Notifications pages
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Menu, 
  Bell, 
  ShoppingCart,
  User, 
  Search, 
  Settings, 
  HelpCircle, 
  LogOut,
  ChevronDown,
  MessageSquare,
  Calendar,
  Clock,
  Activity,
  DollarSign,
  Users,
  Package,
  TrendingUp,
  Shield,
  Sun,
  Moon,
  Monitor,
  ChevronRight,
} from 'lucide-react';

export default function Header({ onOpenMobileMenu }) {
  const navigate = useNavigate();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showTheme, setShowTheme] = useState(false);

  // Mock notifications data
  const notifications = [
    { id: 1, title: 'New Order Received', message: 'Order #ORD-006 from Mega Construction', time: '5 min ago', type: 'order', read: false },
    { id: 2, title: 'Quotation Accepted', message: 'Q-003 accepted by Coastal Builders', time: '15 min ago', type: 'quotation', read: false },
    { id: 3, title: 'Payment Received', message: 'Payment of 850,000 TZS from XYZ Developers', time: '1 hour ago', type: 'payment', read: false },
    { id: 4, title: 'Low Stock Alert', message: 'Portland Cement 50kg is below reorder level', time: '2 hours ago', type: 'inventory', read: true },
    { id: 5, title: 'Delivery Completed', message: 'Dispatch #DSP-001 delivered successfully', time: '3 hours ago', type: 'delivery', read: true },
  ];

  const unreadCount = notifications.filter(n => !n.read).length;

  // Navigation handlers
  const handleViewAllNotifications = () => {
    setShowNotifications(false);
    navigate('/notifications');
  };

  const handleViewProfile = () => {
    setShowProfile(false);
    navigate('/profile');
  };

  const handleViewSettings = () => {
    setShowProfile(false);
    navigate('/settings');
  };

  const handleViewSecurity = () => {
    setShowProfile(false);
    navigate('/security');
  };

  const handleLogout = () => {
    setShowProfile(false);
    // Handle logout logic here
    navigate('/login');
  };

  // Navigate to notification detail
  const handleNotificationClick = (notifId) => {
    setShowNotifications(false);
    navigate(`/notifications/${notifId}`);
  };

  return (
    <header className="sticky top-0 z-30 bg-white border-b border-slate-200 shadow-sm">
      <div className="flex h-16 items-center justify-between px-4 lg:px-6">
        {/* Left Section - Mobile Menu & Quick Stats */}
        <div className="flex items-center gap-4">
          <button
            onClick={onOpenMobileMenu}
            className="lg:hidden text-slate-500 hover:text-slate-700 transition-colors p-1.5 rounded-lg hover:bg-slate-100"
            aria-label="Open menu"
          >
            <Menu size={20} />
          </button>
        </div>

        {/* Right Section */}
        <div className="flex items-center gap-1">
          {/* Date/Time */}
          <div className="hidden lg:flex items-center gap-1 text-xs text-slate-500 mr-2">
            <Clock size={14} />
            <span>{new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
            <span className="text-slate-300">|</span>
            <span>{new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
          </div>

          {/* Notifications */}
          <div className="relative">
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
              aria-label="Notifications"
            >
              <Bell size={18} />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 h-4 w-4 text-[10px] font-bold bg-rose-500 text-white rounded-full flex items-center justify-center ring-2 ring-white">
                  {unreadCount}
                </span>
              )}
            </button>

            {/* Notifications Dropdown */}
            {showNotifications && (
              <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden z-50">
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                  <h4 className="text-sm font-semibold text-slate-800">Notifications</h4>
                  <div className="flex items-center gap-2">
                    <button className="text-xs text-indigo-600 hover:text-indigo-800 font-medium">
                      Mark all read
                    </button>
                  </div>
                </div>
                <div className="max-h-72 overflow-y-auto">
                  {notifications.slice(0, 4).map((notif) => (
                    <div 
                      key={notif.id} 
                      onClick={() => handleNotificationClick(notif.id)}
                      className={`px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors cursor-pointer ${!notif.read ? 'bg-indigo-50/30' : ''}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`mt-0.5 w-2 h-2 rounded-full ${!notif.read ? 'bg-indigo-500' : 'bg-slate-300'}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800">{notif.title}</p>
                          <p className="text-xs text-slate-500 truncate">{notif.message}</p>
                          <p className="text-[10px] text-slate-400 mt-1">{notif.time}</p>
                        </div>
                        {!notif.read && (
                          <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 shrink-0 mt-1.5" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {notifications.length > 4 && (
                  <div className="px-3 py-1.5 text-center border-t border-slate-100">
                    <span className="text-[10px] text-slate-400">
                      +{notifications.length - 4} more notifications
                    </span>
                  </div>
                )}
                <div className="px-4 py-2.5 border-t border-slate-100 text-center">
                  <button 
                    onClick={handleViewAllNotifications}
                    className="text-xs text-indigo-600 hover:text-indigo-800 font-medium flex items-center justify-center gap-1"
                  >
                    View all notifications
                    <ChevronRight size={12} />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Help */}
          <button className="hidden lg:flex p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
            <HelpCircle size={18} />
          </button>

          {/* Theme Toggle */}
          <div className="relative">
            <button
              onClick={() => setShowTheme(!showTheme)}
              className="hidden lg:flex p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
            >
              <Sun size={18} />
            </button>
            {showTheme && (
              <div className="absolute right-0 top-full mt-2 w-40 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden z-50 py-1">
                <button className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors">
                  <Sun size={16} /> Light
                </button>
                <button className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors">
                  <Moon size={16} /> Dark
                </button>
                <button className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors">
                  <Monitor size={16} /> System
                </button>
              </div>
            )}
          </div>

          {/* Profile */}
          <div className="relative">
            <button
              onClick={() => setShowProfile(!showProfile)}
              className="flex items-center gap-2 rounded-lg p-1.5 hover:bg-slate-100 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500/30 ml-1"
              aria-label="Profile"
            >
              <div className="h-8 w-8 rounded-full bg-gradient-to-br from-indigo-600 to-indigo-400 text-white flex items-center justify-center text-sm font-semibold">
                JD
              </div>
              <div className="hidden lg:block text-left">
                <p className="text-xs font-medium text-slate-800">John Doe</p>
                <p className="text-[10px] text-slate-400">Sales Executive</p>
              </div>
              <ChevronDown size={14} className="hidden lg:block text-slate-400" />
            </button>

            {/* Profile Dropdown */}
            {showProfile && (
              <div className="absolute right-0 top-full mt-2 w-64 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden z-50">
                {/* Profile Header */}
                <div className="px-4 py-4 border-b border-slate-100 bg-gradient-to-r from-indigo-50 to-blue-50">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-full bg-gradient-to-br from-indigo-600 to-indigo-400 text-white flex items-center justify-center text-base font-semibold">
                      JD
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-800">John Doe</p>
                      <p className="text-xs text-slate-500">john.doe@company.co.tz</p>
                      <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[10px] font-medium rounded-full">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                          Active
                        </span>
                        <span className="inline-flex px-2 py-0.5 bg-indigo-50 text-indigo-700 text-[10px] font-medium rounded-full">
                          Admin
                        </span>
                        <span className="inline-flex px-2 py-0.5 bg-amber-50 text-amber-700 text-[10px] font-medium rounded-full">
                          Sales
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Menu Items */}
                <div className="py-1">
                  <button 
                    onClick={handleViewProfile}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors group"
                  >
                    <User size={15} className="text-slate-400 group-hover:text-indigo-600" />
                    <span>My Profile</span>
                    <ChevronRight size={14} className="ml-auto text-slate-300" />
                  </button>
                  <button 
                    onClick={handleViewSettings}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors group"
                  >
                    <Settings size={15} className="text-slate-400 group-hover:text-indigo-600" />
                    <span>Settings</span>
                    <ChevronRight size={14} className="ml-auto text-slate-300" />
                  </button>
                  <button 
                    onClick={handleViewSecurity}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors group"
                  >
                    <Shield size={15} className="text-slate-400 group-hover:text-indigo-600" />
                    <span>Security</span>
                    <ChevronRight size={14} className="ml-auto text-slate-300" />
                  </button>
                </div>

                {/* Quick Stats */}
                <div className="border-t border-slate-100 px-4 py-3">
                  <div className="grid grid-cols-3 gap-2">
                    <div className="text-center">
                      <p className="text-xs font-semibold text-slate-800">28</p>
                      <p className="text-[9px] text-slate-400">Orders</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs font-semibold text-slate-800">14</p>
                      <p className="text-[9px] text-slate-400">Leads</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs font-semibold text-slate-800">3</p>
                      <p className="text-[9px] text-slate-400">Quotes</p>
                    </div>
                  </div>
                </div>

                {/* Logout */}
                <div className="border-t border-slate-100 py-1">
                  <button 
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-rose-600 hover:bg-rose-50 transition-colors group"
                  >
                    <LogOut size={15} className="text-rose-400 group-hover:text-rose-600" />
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}